import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, ChatCircleText, CheckCircle, Trash, PencilSimple, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const SEVERITY_STYLES = {
  critical: 'bg-red-100 text-red-700',
  major: 'bg-orange-100 text-orange-700',
  minor: 'bg-yellow-100 text-yellow-700',
};

const STATUS_STYLES = {
  open: 'bg-gray-100 text-gray-700',
  investigating: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-200 text-gray-600',
};

const COMPLAINT_TYPES = ['quality', 'delivery', 'packaging', 'documentation', 'other'];

export default function ComplaintsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'qa_manager' || user?.access_level === 'edit';
  const [complaints, setComplaints] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingComplaint, setEditingComplaint] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        api.get('/complaints'),
        api.get('/apqp/projects').catch(() => ({ data: [] })),
      ]);
      setComplaints(cRes.data);
      setProjects(pRes.data);
    } catch (e) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      customer_name: fd.get('customer_name'),
      customer_contact: fd.get('customer_contact') || null,
      product_part_number: fd.get('product_part_number'),
      complaint_date: fd.get('complaint_date'),
      complaint_type: fd.get('complaint_type'),
      description: fd.get('description'),
      severity: fd.get('severity'),
      project_id: fd.get('project_id') || null,
    };
    try {
      if (editingComplaint) {
        await api.put(`/complaints/${editingComplaint.id}`, payload);
        toast.success('Complaint updated');
      } else {
        await api.post('/complaints', payload);
        toast.success('Complaint logged');
      }
      setShowModal(false);
      setEditingComplaint(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save complaint');
    }
  };

  const handleResolve = async (id) => {
    const resolution = window.prompt('Enter resolution details:');
    if (!resolution || !resolution.trim()) return;
    try {
      await api.patch(`/complaints/${id}/resolve`, null, { params: { resolution: resolution.trim() } });
      toast.success('Complaint resolved');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this complaint?')) return;
    try {
      await api.delete(`/complaints/${id}`);
      toast.success('Complaint deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openEdit = (c) => {
    setEditingComplaint(c);
    setShowModal(true);
  };

  const openCreate = () => {
    setEditingComplaint(null);
    setShowModal(true);
  };

  const filtered = filterStatus === 'all' ? complaints : complaints.filter(c => c.status === filterStatus);

  return (
    <div data-testid="complaints-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Customer Complaints</h1>
          <p className="mt-1 text-sm text-gray-600">Log and track customer complaints across projects</p>
        </div>
        {canEdit && (
          <button
            data-testid="create-complaint-button"
            onClick={openCreate}
            className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors"
          >
            <Plus size={20} weight="bold" />
            Log Complaint
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {['all', 'open', 'investigating', 'resolved', 'closed'].map((s) => (
          <button
            key={s}
            data-testid={`complaint-filter-${s}`}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 text-sm font-medium rounded-sm border transition-colors ${
              filterStatus === s ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading complaints...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-sm">
          <ChatCircleText size={40} className="mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">No complaints found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c, idx) => (
            <div
              key={c.id}
              data-testid={`complaint-card-${idx}`}
              className="bg-white border border-gray-200 rounded-sm p-6"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-mono px-2 py-1 bg-gray-100 text-gray-700 rounded-sm">
                      {c.complaint_number}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">{c.customer_name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-sm ${SEVERITY_STYLES[c.severity] || 'bg-gray-100 text-gray-700'}`}>
                      {c.severity?.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-sm ${STATUS_STYLES[c.status] || 'bg-gray-100 text-gray-700'}`}>
                      {c.status?.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500 capitalize">{c.complaint_type}</span>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">{c.description}</p>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                    <span>Part #: <span className="text-gray-700 font-medium">{c.product_part_number}</span></span>
                    <span>Date: {new Date(c.complaint_date).toLocaleDateString()}</span>
                    {c.customer_contact && <span>Contact: {c.customer_contact}</span>}
                  </div>
                  {c.resolution && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-sm">
                      <p className="text-xs font-medium text-green-900 mb-1">RESOLUTION</p>
                      <p className="text-sm text-green-800">{c.resolution}</p>
                      {c.resolved_date && (
                        <p className="text-xs text-green-700 mt-1">Resolved on {new Date(c.resolved_date).toLocaleDateString()}</p>
                      )}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex gap-2 flex-wrap">
                    {c.status !== 'resolved' && c.status !== 'closed' && (
                      <button
                        data-testid={`resolve-complaint-${idx}`}
                        onClick={() => handleResolve(c.id)}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-sm hover:bg-green-700 transition-colors flex items-center gap-1"
                      >
                        <CheckCircle size={16} /> Resolve
                      </button>
                    )}
                    <button
                      data-testid={`edit-complaint-${idx}`}
                      onClick={() => openEdit(c)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-sm hover:bg-gray-200 transition-colors flex items-center gap-1"
                    >
                      <PencilSimple size={16} />
                    </button>
                    {(user?.role === 'admin' || user?.role === 'qa_manager') && (
                      <button
                        data-testid={`delete-complaint-${idx}`}
                        onClick={() => handleDelete(c.id)}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-sm hover:bg-red-700 transition-colors flex items-center gap-1"
                      >
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingComplaint ? 'Edit Complaint' : 'Log Customer Complaint'}
              </h2>
              <button onClick={() => { setShowModal(false); setEditingComplaint(null); }} className="p-1 hover:bg-gray-100 rounded-sm">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">CUSTOMER NAME *</label>
                  <input
                    name="customer_name"
                    data-testid="complaint-customer-name"
                    defaultValue={editingComplaint?.customer_name || ''}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">CONTACT</label>
                  <input
                    name="customer_contact"
                    data-testid="complaint-customer-contact"
                    defaultValue={editingComplaint?.customer_contact || ''}
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">PART NUMBER *</label>
                  <input
                    name="product_part_number"
                    data-testid="complaint-part-number"
                    defaultValue={editingComplaint?.product_part_number || ''}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">COMPLAINT DATE *</label>
                  <input
                    name="complaint_date"
                    data-testid="complaint-date"
                    type="date"
                    defaultValue={editingComplaint?.complaint_date || new Date().toISOString().slice(0, 10)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">COMPLAINT TYPE *</label>
                  <select
                    name="complaint_type"
                    data-testid="complaint-type"
                    defaultValue={editingComplaint?.complaint_type || 'quality'}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    {COMPLAINT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">SEVERITY *</label>
                  <select
                    name="severity"
                    data-testid="complaint-severity"
                    defaultValue={editingComplaint?.severity || 'major'}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="critical">Critical</option>
                    <option value="major">Major</option>
                    <option value="minor">Minor</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-900 mb-2">LINKED PROJECT (Optional)</label>
                  <select
                    name="project_id"
                    data-testid="complaint-project"
                    defaultValue={editingComplaint?.project_id || ''}
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="">— None —</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.project_name} ({p.part_number})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">DESCRIPTION *</label>
                <textarea
                  name="description"
                  data-testid="complaint-description"
                  rows={4}
                  defaultValue={editingComplaint?.description || ''}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingComplaint(null); }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="complaint-submit"
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  {editingComplaint ? 'Update' : 'Log Complaint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
